// Pure helpers of the approval ("visa") screens (GED-FE-06): the shapes the screens share, what the
// caller may do with a flow, the steps draft of the request dialog and of the templates, the
// plain-language summary of "who decides when" and the messages for the approval routes.
import type {
  ApprovalTemplate,
  ApprovalDecisionValue,
  ApprovalFlowStatus,
} from '@batiflow/contracts';
import type { FlowDtoOutput, StartDto } from '@batiflow/api-client';
import { ApiError } from '@batiflow/client-core';
import { intlLocale, type AppLocale } from '@batiflow/i18n';
import type { StatusIntent } from '@batiflow/ui';
import type { TFunction } from 'i18next';
import { apiErrorCode } from '../model';

export type ApprovalFlow = FlowDtoOutput;
export type ApprovalStep = FlowDtoOutput['steps'][number];
export type Translate = TFunction<'documents'>;

export type { ApprovalDecisionValue, ApprovalFlowStatus, ApprovalTemplate };

export const DECISIONS: readonly ApprovalDecisionValue[] = ['vso', 'vao', 'ref'];
export const FLOW_STATUSES: readonly ApprovalFlowStatus[] = [
  'in_progress',
  'approved',
  'approved_with_comments',
  'rejected',
  'cancelled',
];

/** The API's limits (contracts/documents/approvals.ts). */
export const MAX_STEPS = 10;
export const MAX_REVIEWERS = 20;
/** Partner offices a step can name (COLX-BE-02). */
export const MAX_OFFICES = 5;
export const COMMENT_MAX = 2000;

export const isDecision = (value: string): value is ApprovalDecisionValue =>
  (DECISIONS as readonly string[]).includes(value);

// --- What the caller may do with a flow (its `_permissions`).

interface WithFlowPermissions {
  _permissions: readonly string[];
}

export const canDecide = (flow: WithFlowPermissions): boolean =>
  flow._permissions.includes('can_decide');
export const canCancel = (flow: WithFlowPermissions): boolean =>
  flow._permissions.includes('can_cancel');

/** The step that waits for decisions now (a running flow has exactly one). */
export const activeStep = (flow: Pick<ApprovalFlow, 'steps'>): ApprovalStep | null =>
  flow.steps.find((step) => step.status === 'active') ?? null;

/** Position of the active step (1-based) and the number of steps, for "Step 2 of 3". */
export function stepProgress(flow: Pick<ApprovalFlow, 'steps'>): {
  current: number;
  total: number;
} {
  const active = activeStep(flow);
  return { current: active?.ordinal ?? flow.steps.length, total: flow.steps.length };
}

/** The flow of a list that is newest: lists come newest first, but never rely on it. */
export function latestFlow<T extends Pick<ApprovalFlow, 'createdAt'>>(
  flows: readonly T[],
): T | null {
  let latest: T | null = null;
  for (const flow of flows) {
    if (!latest || flow.createdAt > latest.createdAt) latest = flow;
  }
  return latest;
}

/** A reviewer of a sequential step is "on turn" when everyone listed before them has decided. */
export function reviewerState(
  step: ApprovalStep,
  reviewerId: string,
): 'decided' | 'turn' | 'waiting' | 'later' | 'closed' {
  if (step.decisions.some((decision) => decision.reviewerId === reviewerId)) return 'decided';
  if (step.status === 'cancelled' || step.status === 'decided') return 'closed';
  if (step.status === 'waiting') return 'later';
  if (step.mode === 'parallel') return 'waiting';
  const next = step.reviewers.find(
    (reviewer) => !step.decisions.some((decision) => decision.reviewerId === reviewer.id),
  );
  return next?.id === reviewerId ? 'turn' : 'waiting';
}

// --- Status and decision presentation (text and icon always; colour only reinforces).

export const FLOW_STATUS_INTENT: Record<ApprovalFlowStatus, StatusIntent> = {
  in_progress: 'informative',
  approved: 'success',
  approved_with_comments: 'warning',
  rejected: 'danger',
  cancelled: 'neutral',
};

export const DECISION_INTENT: Record<ApprovalDecisionValue, StatusIntent> = {
  vso: 'success',
  vao: 'warning',
  ref: 'danger',
};

// --- Steps draft (request dialog, templates).

export type StepMode = 'sequential' | 'parallel';
export const STEP_MODES: readonly StepMode[] = ['sequential', 'parallel'];

export interface StepDraft {
  /** Local identity of the step in the editor (the order changes). */
  key: string;
  mode: StepMode;
  /** Reviewers' user ids; the order matters in a sequential step. */
  reviewerIds: string[];
  /** Partner offices whose approvers in the space are asked (COLX-BE-02). */
  reviewerOrgIds: string[];
}

let draftCounter = 0;
/** A new empty step: sequential, no reviewer yet. */
export const newStep = (mode: StepMode = 'sequential'): StepDraft => {
  draftCounter += 1;
  return { key: `step-${String(draftCounter)}`, mode, reviewerIds: [], reviewerOrgIds: [] };
};

/** A draft built from stored steps (a template's). */
export const draftOf = (
  steps: readonly {
    mode: StepMode;
    reviewerIds: readonly string[];
    reviewerOrgIds?: readonly string[];
  }[],
): StepDraft[] =>
  steps.map((step) => ({
    ...newStep(step.mode),
    reviewerIds: [...step.reviewerIds],
    reviewerOrgIds: [...(step.reviewerOrgIds ?? [])],
  }));

/** The body of the steps in a request (without the editor's keys); offices only when named. */
export const stepsBody = (
  steps: readonly StepDraft[],
): { mode: StepMode; reviewerIds: string[]; reviewerOrgIds?: string[] }[] =>
  steps.map(({ mode, reviewerIds, reviewerOrgIds }) => ({
    mode,
    reviewerIds: [...reviewerIds],
    ...(reviewerOrgIds.length > 0 ? { reviewerOrgIds: [...reviewerOrgIds] } : {}),
  }));

export const moveItem = <T>(items: readonly T[], from: number, to: number): T[] => {
  if (to < 0 || to >= items.length || from === to) return [...items];
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved as T);
  return next;
};

/** Why a draft cannot be sent yet; `null` when it can. */
export function draftProblem(steps: readonly StepDraft[]): 'noReviewer' | null {
  return steps.length === 0 ||
    steps.some((step) => step.reviewerIds.length + step.reviewerOrgIds.length === 0)
    ? 'noReviewer'
    : null;
}

/** The request body: a template, or the steps; the due date only when given. */
export function startBody(
  source: { templateId: string } | { steps: readonly StepDraft[] },
  dueOn: string,
): StartDto {
  return {
    ...('templateId' in source
      ? { templateId: source.templateId }
      : { steps: stepsBody(source.steps) }),
    ...(dueOn ? { dueOn } : {}),
  };
}

/** One line of the "who decides when" summary. */
export interface StepSummary {
  ordinal: number;
  mode: StepMode;
  names: string[];
}

/**
 * The steps as lines of the summary, reviewer names resolved with `nameOf`; a partner office is
 * phrased by `officeOf` (its approvers in the space are the ones asked).
 */
export function summarize(
  steps: readonly {
    mode: StepMode;
    reviewerIds: readonly string[];
    reviewerOrgIds?: readonly string[];
  }[],
  nameOf: (userId: string) => string,
  officeOf: (orgId: string) => string = (orgId) => orgId,
): StepSummary[] {
  return steps.map((step, index) => ({
    ordinal: index + 1,
    mode: step.mode,
    names: [...step.reviewerIds.map(nameOf), ...(step.reviewerOrgIds ?? []).map(officeOf)],
  }));
}

/** Names of the offices met in stored templates (id to name; a name can be unknown). */
export function officeNamesOf(
  templates: readonly {
    steps: readonly { reviewerOrgs: readonly { id: string; name: string | null }[] }[];
  }[],
): Map<string, string | null> {
  const names = new Map<string, string | null>();
  for (const template of templates)
    for (const step of template.steps)
      for (const office of step.reviewerOrgs) names.set(office.id, office.name);
  return names;
}

/** "A, B and C" in the reader's language. */
export const listOf = (locale: AppLocale, names: readonly string[]): string =>
  new Intl.ListFormat(intlLocale(locale), { style: 'long', type: 'conjunction' }).format(names);

// --- Decision form.

/** VAO and REF need an observation: a comment, or at least one linked annotation. */
export const needsObservation = (decision: ApprovalDecisionValue): boolean => decision !== 'vso';

export function decisionBody(
  decision: ApprovalDecisionValue,
  comment: string,
  annotationIds: readonly string[],
): { decision: ApprovalDecisionValue; comment?: string; annotationIds?: string[] } {
  const text = comment.trim();
  return {
    decision,
    ...(text ? { comment: text } : {}),
    ...(annotationIds.length > 0 ? { annotationIds: [...annotationIds] } : {}),
  };
}

/** True when the form has what the decision needs. */
export const hasObservation = (comment: string, annotationIds: readonly string[]): boolean =>
  comment.trim().length > 0 || annotationIds.length > 0;

// --- Server answers.

/** The code of a field error (a 422 `VALIDATION_FAILED` carries its detail there), if present. */
const fieldCode = (error: unknown, code: string): boolean =>
  error instanceof ApiError && error.fieldErrors.some((field) => field.code === code);

export const isNotYourTurn = (error: unknown): boolean =>
  apiErrorCode(error) === 'APPROVAL_NOT_YOUR_TURN';
export const isApprovalClosed = (error: unknown): boolean =>
  apiErrorCode(error) === 'APPROVAL_CLOSED';
export const isObservationRequired = (error: unknown): boolean =>
  fieldCode(error, 'OBSERVATION_REQUIRED');

/** A plain-language message for an error of an approval route (never the raw server text). */
export function approvalErrorMessage(t: Translate, error: unknown): string {
  if (!(error instanceof ApiError)) return t('common:errors.generic');
  if (apiErrorCode(error) === 'DOCUMENT_LOCKED') return t('approvals.errors.locked');
  if (apiErrorCode(error) === 'APPROVAL_ORG_HAS_NO_REVIEWER')
    return t('approvals.errors.officeNoReviewer');
  if (isNotYourTurn(error)) return t('approvals.errors.notYourTurn');
  if (isApprovalClosed(error)) return t('approvals.errors.closed');
  if (error.code === 'VALIDATION_FAILED') {
    if (isObservationRequired(error)) return t('approvals.errors.observationRequired');
    if (fieldCode(error, 'REVIEWER_CANNOT_VIEW')) return t('approvals.errors.reviewerCannotView');
    if (fieldCode(error, 'VERSION_NOT_AVAILABLE')) return t('approvals.errors.versionNotAvailable');
    if (fieldCode(error, 'APPROVAL_STEPS_INVALID')) return t('approvals.errors.stepsInvalid');
    if (fieldCode(error, 'PARTNER_OFFICE_UNKNOWN')) return t('approvals.errors.officeUnknown');
    return t('approvals.errors.invalid');
  }
  if (error.code === 'FORBIDDEN') return t('approvals.errors.forbidden');
  if (error.code === 'NOT_FOUND') return t('approvals.errors.notFound');
  return t(`common:errors.${error.code}`, { defaultValue: t('common:errors.generic') });
}

/** The label of a document in a sentence or a heading: its code and title, or the title alone. */
export const documentLabel = (code: string | null, title: string): string =>
  code ? `${code} — ${title}` : title;

/** Today's date (`YYYY-MM-DD`) in Tunis, the earliest due date a request offers. */
export const todayInTunis = (now: Date = new Date()): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Tunis',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
